import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Layout from '../../components/Layout';
import AddButton from '../../components/Button/AddButton';
import AlertModal from '../../components/AlertModal';
import { useMst } from '../../model/Root';
import {
  deleteYoutubePlaylist,
  getYoutubePlaylists,
  setYoutubePlaylistEnabled,
  updateYoutubePlaylist,
} from '../../services/youtubePlaylistsApi';
import type { YoutubePlaylist, YoutubePlaylistQuery } from '../../types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';

type EnabledFilter = 'all' | 'enabled' | 'disabled';

type FiltersState = {
  search: string;
  enabled: EnabledFilter;
  churchId: string;
};

const DEFAULT_FILTERS: FiltersState = {
  search: '',
  enabled: 'all',
  churchId: 'all',
};

type ResolvedPlaylist = YoutubePlaylist & {
  churchName?: string | null;
  title: string;
};

const compactId = (value: string, head = 8, tail = 4) => {
  const v = (value ?? '').trim();
  if (v.length <= head + tail + 1) return v;
  return `${v.slice(0, head)}…${v.slice(-tail)}`;
};

const buildQuery = (filters: FiltersState): YoutubePlaylistQuery => {
  const trimmedSearch = filters.search.trim();
  return {
    search: trimmedSearch ? trimmedSearch : undefined,
    enabled: filters.enabled === 'all' ? undefined : filters.enabled === 'enabled',
    churchId: filters.churchId === 'all' ? undefined : filters.churchId,
  };
};

const YoutubePlaylistsScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    churchStore: { churches, fetchChurches },
    errorStore: { setError },
  } = useMst();

  const [items, setItems] = useState<YoutubePlaylist[]>([]);
  const [formFilters, setFormFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [reorderDraft, setReorderDraft] = useState<ResolvedPlaylist[]>([]);

  const loadItems = useCallback(
    async (query: YoutubePlaylistQuery) => {
      setLoading(true);
      try {
        const data = await getYoutubePlaylists(query);
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setError('general_error_message');
      } finally {
        setLoading(false);
        setFetchedOnce(true);
      }
    },
    [setError]
  );

  useEffect(() => {
    if (churches.length === 0) {
      void fetchChurches('', true);
    }
  }, [churches.length, fetchChurches]);

  useEffect(() => {
    const query = buildQuery(appliedFilters);
    void loadItems(query);
  }, [appliedFilters, loadItems, location.key]);

  const applyFilters = () => {
    setAppliedFilters({ ...formFilters });
  };

  const refresh = async () => {
    const query = buildQuery(appliedFilters);
    await loadItems(query);
  };

  const askDelete = (id?: number | null) => {
    if (!id) return;
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteYoutubePlaylist(deleteId);
      setItems((prev) => prev.filter((item) => item.id !== deleteId));
    } catch (err) {
      console.error(err);
      setError('general_error_message');
    } finally {
      cancelDelete();
    }
  };

  const toggleEnabled = async (id: number, enabled: boolean) => {
    try {
      await setYoutubePlaylistEnabled(id, enabled);
      setItems((prev) => prev.map((item) => (item.id === id ? { ...item, enabled } : item)));
    } catch (err) {
      console.error(err);
      setError('general_error_message');
      await refresh();
    }
  };

  const resolved: ResolvedPlaylist[] = useMemo(() => {
    const withMeta: ResolvedPlaylist[] = items.map((item) => {
      const churchName =
        item.churchId && churches.length > 0
          ? (churches.find((c) => c.id === item.churchId)?.name ?? item.churchId)
          : item.churchId;
      const title = item.displayName?.trim() ? item.displayName : item.playlistId;
      return { ...item, churchName, title };
    });

    return withMeta.sort((a, b) => {
      const aOrder = Number.isFinite(a.sortOrder) ? a.sortOrder : 0;
      const bOrder = Number.isFinite(b.sortOrder) ? b.sortOrder : 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.title.localeCompare(b.title);
    });
  }, [items, churches]);

  const isDefaultAppliedFilters =
    appliedFilters.search.trim().length === 0 && appliedFilters.enabled === 'all' && appliedFilters.churchId === 'all';

  const canOpenReorder = isDefaultAppliedFilters && resolved.every((p) => !!p.id);

  const openReorder = () => {
    setReorderDraft(resolved);
    setReorderOpen(true);
  };

  const closeReorder = () => {
    if (reorderSaving) return;
    setReorderOpen(false);
    setReorderDraft([]);
  };

  const moveDraft = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= reorderDraft.length || toIndex >= reorderDraft.length) return;
    const next = reorderDraft.slice();
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    setReorderDraft(next);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    moveDraft(result.source.index, result.destination.index);
  };

  const saveReorder = async () => {
    if (!canOpenReorder) return;
    setReorderSaving(true);
    try {
      const updates = reorderDraft
        .map((item, index) => {
          if (!item.id) return null;
          const prevSort = resolved.find((p) => p.id === item.id)?.sortOrder ?? 0;
          if (Number(prevSort) === index) return null;
          return { id: item.id, sortOrder: index };
        })
        .filter(Boolean) as Array<{ id: number; sortOrder: number }>;

      if (updates.length > 0) {
        await Promise.all(updates.map((u) => updateYoutubePlaylist(u.id, { sortOrder: u.sortOrder })));
      }

      // Update local list ordering immediately (and keep the same data fields).
      setItems(
        reorderDraft.map((p, index) => ({
          id: p.id,
          playlistId: p.playlistId,
          playlistUrl: p.playlistUrl,
          displayName: p.displayName,
          churchId: p.churchId,
          enabled: p.enabled,
          sortOrder: index,
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
        }))
      );

      closeReorder();
    } catch (err) {
      console.error(err);
      setError('general_error_message');
      await refresh();
    } finally {
      setReorderSaving(false);
    }
  };

  return (
    <Layout title={t('youtubePlaylists.title') ?? 'YouTube playlists'} fullWidth>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={5}>
          <TextField
            size="small"
            fullWidth
            label={t('search') ?? 'Search'}
            value={formFilters.search}
            onChange={(e) => setFormFilters((prev) => ({ ...prev, search: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters();
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="youtube-playlists-enabled-filter">{t('status') ?? 'Status'}</InputLabel>
            <Select
              labelId="youtube-playlists-enabled-filter"
              label={t('status') ?? 'Status'}
              value={formFilters.enabled}
              onChange={(e) => setFormFilters((prev) => ({ ...prev, enabled: e.target.value as EnabledFilter }))}
            >
              <MenuItem value="all">{t('all') ?? 'All'}</MenuItem>
              <MenuItem value="enabled">{t('active') ?? 'Enabled'}</MenuItem>
              <MenuItem value="disabled">{t('inactive') ?? 'Disabled'}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="youtube-playlists-church-filter">{t('church.title') ?? 'Church'}</InputLabel>
            <Select
              labelId="youtube-playlists-church-filter"
              label={t('church.title') ?? 'Church'}
              value={formFilters.churchId}
              onChange={(e) => setFormFilters((prev) => ({ ...prev, churchId: e.target.value as string }))}
            >
              <MenuItem value="all">{t('all') ?? 'All'}</MenuItem>
              {churches.map((c, index) => (
                <MenuItem key={c.id ?? `${c.name ?? 'church'}-${index}`} value={c.id ?? ''}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={applyFilters}>
                {t('filter') ?? 'Filter'}
              </Button>
              <Button variant="outlined" onClick={openReorder} disabled={!canOpenReorder}>
                {t('youtubePlaylists.reorderButton') ?? 'Reorder'}
              </Button>
              <AddButton
                onClick={() => navigate('/programs/youtube-playlists/create')}
                text={t('youtubePlaylists.add') ?? 'Add YouTube playlist'}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {fetchedOnce && loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="GrayText">
            {t('loading') ?? 'Loading'}
          </Typography>
        </Box>
      )}

      {!fetchedOnce && loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">{t('loading') ?? 'Loading'}</Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160 }}>{t('youtubePlaylists.displayName') ?? 'Name'}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 140 }}>
                  {t('youtubePlaylists.playlistId') ?? 'Playlist ID'}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, minWidth: 90 }}>
                  {t('youtubePlaylists.playlistUrl') ?? 'Playlist URL'}
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>{t('church.title') ?? 'Church'}</TableCell>
                <TableCell sx={{ width: 90 }}>{t('enabled') ?? 'Enabled'}</TableCell>
                <TableCell sx={{ width: 140 }}>{t('actions') ?? 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resolved.map((item) => {
                const fullId = item.playlistId ?? '';
                const shortId = compactId(fullId);
                return (
                  <TableRow key={item.id ?? item.playlistId} hover>
                    <TableCell>
                      <Tooltip title={item.title} placement="top" arrow>
                        <Typography noWrap sx={{ maxWidth: { xs: 220, md: 320 } }}>
                          {item.displayName?.trim() ? item.displayName : item.playlistId}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title={fullId} placement="top" arrow>
                        <Typography noWrap sx={{ maxWidth: 220, fontFamily: 'monospace' }}>
                          {shortId}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {item.playlistUrl ? (
                        <Box component="a" href={item.playlistUrl} target="_blank" rel="noreferrer">
                          {t('youtubePlaylists.open') ?? 'Open'}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography noWrap sx={{ maxWidth: { xs: 160, md: 220 } }}>
                        {item.churchName ?? t('youtubePlaylists.noChurch') ?? 'No church'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Switch
                        checked={!!item.enabled}
                        onChange={(event) => {
                          if (!item.id) return;
                          void toggleEnabled(item.id, event.target.checked);
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          if (!item.id) return;
                          navigate(`/programs/youtube-playlists/${item.id}`);
                        }}
                      >
                        {t('edit') ?? 'Edit'}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => askDelete(item.id ?? null)}
                        aria-label="delete"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {resolved.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('youtubePlaylists.empty') ?? 'No YouTube playlists yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={reorderOpen} onClose={closeReorder} fullWidth maxWidth="sm">
        <DialogTitle>{t('youtubePlaylists.reorderTitle') ?? 'Reorder playlists'}</DialogTitle>
        <DialogContent dividers>
          {!isDefaultAppliedFilters && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('youtubePlaylists.reorderDisabledHelp') ?? 'Clear filters before reordering.'}
            </Typography>
          )}
          {isDefaultAppliedFilters && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('youtubePlaylists.reorderHelp') ?? 'Drag to reorder. Use arrows for precise moves. Save when ready.'}
            </Typography>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="youtube-playlists-reorder">
              {(dropProvided) => (
                <List ref={dropProvided.innerRef} {...dropProvided.droppableProps} sx={{ py: 0 }}>
                  {reorderDraft.map((item, index) => (
                    <Draggable
                      key={String(item.id ?? item.playlistId)}
                      draggableId={String(item.id ?? item.playlistId)}
                      index={index}
                    >
                      {(dragProvided, snapshot) => (
                        <ListItem
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          divider
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => moveDraft(index, index - 1)}
                                disabled={index === 0 || reorderSaving}
                                aria-label={t('youtubePlaylists.moveUp') ?? 'Move up'}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveDraft(index, index + 1)}
                                disabled={index === reorderDraft.length - 1 || reorderSaving}
                                aria-label={t('youtubePlaylists.moveDown') ?? 'Move down'}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                          sx={{
                            bgcolor: snapshot.isDragging ? 'action.hover' : 'transparent',
                          }}
                        >
                          <Box
                            {...dragProvided.dragHandleProps}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mr: 1,
                              cursor: 'grab',
                              color: 'text.secondary',
                            }}
                            aria-label={t('youtubePlaylists.reorderHandle') ?? 'Drag to reorder'}
                            title={t('youtubePlaylists.reorderHandle') ?? 'Drag to reorder'}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <Box sx={{ width: 32, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {index + 1}.
                          </Box>
                          <ListItemText
                            primary={item.title}
                            secondary={item.churchName ? `${item.playlistId} • ${item.churchName}` : item.playlistId}
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReorder} disabled={reorderSaving}>
            {t('cancel') ?? 'Cancel'}
          </Button>
          <Button onClick={saveReorder} variant="contained" disabled={reorderSaving || !canOpenReorder}>
            {t('youtubePlaylists.saveOrder') ?? 'Save order'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={confirmOpen}
        onClose={cancelDelete}
        title={'delete'}
        leftButtonPress={cancelDelete}
        rightButtonPress={confirmDelete}
      />
    </Layout>
  );
});

export default YoutubePlaylistsScreen;
